import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { X, Phone, AlertOctagon, Loader2, ExternalLink, MessageCircle } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  { v: "client_injoignable", labelKey: "irm.rClientInjoignable", needsCall: true },
  { v: "adresse_introuvable", labelKey: "irm.rAdresseIntrouvable" },
  { v: "cash_non_paye", labelKey: "irm.rCashNonPaye" },
  { v: "commande_refusee", labelKey: "irm.rCommandeRefusee" },
  { v: "autre", labelKey: "irm.rAutre" },
];

const REQUIRED_WAIT_MIN = 8;

/**
 * Rich incident report modal for drivers.
 * - Radio: mandatory reason
 * - Comment: optional
 * - For "client injoignable": user must click Appeler first, then wait 8 min
 * On submit: POST /api/orders/{id}/incident + on success provides WhatsApp link
 */
export default function IncidentReportModal({ order, open, onClose, onSubmitted }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [calledClient, setCalledClient] = useState(false);
  const [waitStartedAt, setWaitStartedAt] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setReason("");
      setComment("");
      setCalledClient(false);
      setWaitStartedAt(null);
      setResult(null);
    }
  }, [open]);

  useEffect(() => {
    if (!waitStartedAt) return;
    const t = setInterval(() => setNowMs(Date.now()), 500);
    return () => clearInterval(t);
  }, [waitStartedAt]);

  if (!open) return null;

  const needsCall = REASONS.find((r) => r.v === reason)?.needsCall;
  const waitMs = waitStartedAt ? Math.max(0, REQUIRED_WAIT_MIN * 60 * 1000 - (nowMs - waitStartedAt)) : 0;
  const waitSec = Math.ceil(waitMs / 1000);
  const waitBlocked = needsCall && (!calledClient || waitSec > 0);
  const submitDisabled = !reason || busy || waitBlocked;

  const clientPhone = order?.phone || "";
  const telHref = clientPhone ? `tel:${clientPhone.replace(/[^+0-9]/g, "")}` : null;

  const handleCall = () => {
    if (!telHref) {
      toast.error(t("irm.noPhone"));
      return;
    }
    setCalledClient(true);
    if (!waitStartedAt) setWaitStartedAt(Date.now());
    window.location.href = telHref;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = { reason, comment };
      if (reason === "client_injoignable") {
        body.called_client = true;
        body.wait_started_at = new Date(waitStartedAt).toISOString();
      }
      const { data } = await apiClient.post(`/orders/${order.order_id}/incident`, body);
      setResult(data);
      toast.success(t("irm.reported"));
      onSubmitted?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="incident-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-sand px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertOctagon className="w-5 h-5 text-brand-red" />
            <h2 className="font-display text-lg font-semibold">{t("irm.title")}</h2>
          </div>
          <button onClick={onClose} data-testid="incident-close" className="p-2 rounded-full hover:bg-elevated"><X className="w-4 h-4" /></button>
        </div>

        {result ? (
          <div className="p-5 space-y-4">
            <div className="rounded-2xl bg-[#E7F6EE] border border-brand-green/30 p-4">
              <div className="font-display text-lg font-semibold text-brand-green mb-1">{t("irm.ticketCreated", { id: result.incident_id.slice(-6).toUpperCase() })}</div>
              <div className="text-sm text-mute">{t("irm.ticketSub")}</div>
            </div>
            {result.whatsapp_support_link && (
              <a
                href={result.whatsapp_support_link}
                target="_blank" rel="noreferrer"
                data-testid="incident-wa-support"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-[#007A4A] text-white py-3 font-semibold"
              >
                <MessageCircle className="w-4 h-4" /> {t("irm.waSupport")}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <button onClick={onClose} data-testid="incident-done" className="w-full rounded-full border border-sand py-3 text-sm font-semibold">
              {t("irm.close")}
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            <div className="text-xs text-mute">
              {t("irm.order")} <span className="font-mono font-semibold text-ink">#{order?.order_id?.slice(-6).toUpperCase()}</span>
              {order?.restaurant_name && <> · {order.restaurant_name}</>}
            </div>

            <div>
              <div className="text-xs uppercase tracking-widest text-mute mb-2">{t("irm.reasonRequired")}</div>
              <div className="space-y-2">
                {REASONS.map((r) => (
                  <label
                    key={r.v}
                    data-testid={`incident-reason-${r.v}`}
                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${reason === r.v ? "border-brand-red bg-[#FFF5F6]" : "border-sand hover:border-brand-red/50"}`}
                  >
                    <input
                      type="radio"
                      name="reason"
                      checked={reason === r.v}
                      onChange={() => { setReason(r.v); setCalledClient(false); setWaitStartedAt(null); }}
                      className="mt-0.5"
                    />
                    <div>
                      <div className="text-sm font-semibold">{t(r.labelKey)}</div>
                      {r.needsCall && (
                        <div className="text-xs text-mute mt-0.5">
                          {t("irm.needCallHint", { min: REQUIRED_WAIT_MIN })}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {needsCall && (
              <div className="rounded-2xl bg-[#FFF5F6] border border-brand-red/20 p-4 space-y-3" data-testid="incident-call-gate">
                <button
                  type="button"
                  onClick={handleCall}
                  disabled={!telHref}
                  data-testid="incident-call-btn"
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-semibold text-sm ${calledClient ? "bg-brand-green text-white" : "bg-brand-red text-white hover:bg-[#C81F2D]"} disabled:opacity-60`}
                >
                  <Phone className="w-4 h-4" /> {calledClient ? t("irm.called") : t("irm.callClient")}
                </button>
                {calledClient && (
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-widest text-mute">{t("irm.mandatoryWait")}</div>
                    <div className="font-display text-3xl font-bold text-brand-red tabular-nums" data-testid="incident-wait-countdown">
                      {waitSec > 0 ? `${Math.floor(waitSec / 60)}:${String(waitSec % 60).padStart(2, "0")}` : "0:00"}
                    </div>
                    <div className="text-xs text-mute mt-1">
                      {waitSec > 0
                        ? t("irm.canStillAnswer")
                        : t("irm.waitOver")}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <div className="text-xs uppercase tracking-widest text-mute mb-2">{t("irm.comment")}</div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={1000}
                rows={3}
                placeholder={t("irm.commentPh")}
                data-testid="incident-comment"
                className="w-full bg-white border border-sand rounded-xl px-4 py-3 outline-none focus:border-brand-red text-sm"
              />
            </div>

            <div className="flex gap-2 sticky bottom-0 bg-white pt-3">
              <button
                type="button"
                onClick={onClose}
                data-testid="incident-cancel"
                className="flex-1 rounded-full border border-sand py-3 text-sm font-semibold"
              >
                {t("irm.cancel")}
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitDisabled}
                data-testid="incident-submit"
                className="flex-1 rounded-full bg-brand-red hover:bg-[#C81F2D] disabled:opacity-50 text-white py-3 text-sm font-semibold inline-flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertOctagon className="w-4 h-4" />}
                {waitBlocked ? t("irm.waitS", { s: waitSec }) : t("irm.submit")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
